import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { paymentApi } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

type Props = {
  open: boolean;
  orderId: string | null;
  onClose: () => void;
  onSuccess: () => void;
};

export function UpdateOrderStatusDialog({ open, orderId, onClose, onSuccess }: Props) {
  const [orderStatus, setOrderStatus] = useState("PENDING_PAYMENT");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!orderId) return;
    if (!reason.trim()) {
      toast.error("Please provide a reason for this status change");
      return;
    }
    setSaving(true);
    try {
      const result = await paymentApi.updateOrderStatus(orderId, orderStatus, reason);
      if (result.success) {
        toast.success(result.message || "Order status updated");
        onSuccess();
      } else {
        toast.error(result.message || "Failed to update order status");
      }
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to update order status");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Update Order Status</DialogTitle>
          <DialogDescription>Change the status of this order and provide a reason.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          {orderId && (
            <p className="text-sm">
              <strong>Order ID:</strong> {orderId}
            </p>
          )}
          <div className="grid gap-2">
            <Label>New Order Status</Label>
            <Select value={orderStatus} onValueChange={setOrderStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING_PAYMENT">Pending Payment</SelectItem>
                <SelectItem value="PAYMENT_COMPLETED">Payment Completed</SelectItem>
                <SelectItem value="ENROLLED">Enrolled</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Reason for Change</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why you're changing the order status"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || !orderId}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Update Status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
